import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Puff } from 'react-loader-spinner'
import { IoIosAdd } from 'react-icons/io'
import { BiTrash } from 'react-icons/bi'
import Button from '../../components/Button'
import Table from '../../components/Table'
import Confirmation from '../../components/Confirmation/Confirmation'
import GroupNutritionModal from './Modal'
import styles from './styles.module.scss'
import { getGroupNutritionList, getNutritionGroup, deleteGroupNutrition, getParallelsForGroupNutrition } from '../../actions/directoryCanteen'

const TABLE_HEADERS = [
  { key: 'name', label: 'Название' },
  { key: 'sort', label: 'Сортировка' },
  { key: 'actions', label: '', width: '60px' }
]

export default function GroupNutrition() {
  const dispatch = useDispatch()
  const { groupNutritionList, group, loading, parallelList } = useSelector(state => state.directoryCanteen)

  const [selectedGroup, setSelectedGroup] = useState(null)
  const [confirmation, setConfirmation] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [sortField, setSortField] = useState('name')
  const [sortAsc, setSortAsc] = useState(true)

  useEffect(() => {
    dispatch(getGroupNutritionList())
  }, [dispatch])

  useEffect(() => {
    if (group) {
      setSelectedGroup(group)
    }
  }, [group])

  const openModal = useCallback((item) => () => {
    if (item) {
      dispatch(getNutritionGroup(item.xp_key))
      dispatch(getParallelsForGroupNutrition())

    } else {
      setSelectedGroup({
        name: '',
        parallels: [],
        sort: '',
        xp_key: ''
      })
    }
  }, [dispatch])

  const closeModal = useCallback(() => {
    setSelectedGroup(null)
  }, [])

  const handleDelete = useCallback((item) => (e) => {
    e.stopPropagation()
    setConfirmation({ id: item.xp_key, name: item.name })
  }, [])

  const acceptConfirmation = useCallback(async () => {
    if (!confirmation) return
    setIsDeleting(true)
    try {
      await dispatch(deleteGroupNutrition(confirmation.id))
    } finally {
      setConfirmation(null)
      setIsDeleting(false)
    }
  }, [dispatch, confirmation])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(null)
  }, [])

  const handleSort = useCallback((field) => {
    if (sortField === field) setSortAsc(v => !v)
    else { setSortField(field); setSortAsc(true) }
  }, [sortField])

  const sortedList = useMemo(() => {
    if (!groupNutritionList) return []
    return [...groupNutritionList].sort((a, b) => {
      const aVal = a[sortField] ?? ''
      const bVal = b[sortField] ?? ''
      if (typeof aVal === 'number' && typeof bVal === 'number')
        return sortAsc ? aVal - bVal : bVal - aVal
      return sortAsc
        ? String(aVal).localeCompare(String(bVal), 'ru')
        : String(bVal).localeCompare(String(aVal), 'ru')
    })
  }, [groupNutritionList, sortField, sortAsc])

  const isModalOpen = selectedGroup !== null
  const isAddMode = selectedGroup && typeof selectedGroup.xp_key !== 'number'

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <IoIosAdd size={64} className={styles.emptyIcon} />
      <p>Нет добавленных групп питания</p>
      <Button onClick={openModal()}>Добавить первую группу питания</Button>
    </div>
  )

  const renderLoading = () => (
    <div className={styles.loadingState}>
      <Puff
        height={50}
        width={50}
        color="#3689FF"
        ariaLabel="Загрузка..."
      />
      <span>Загрузка групп питания...</span>
    </div>
  )

  const renderTable = () => (
    <div
      className={styles.tableContainer}
      style={{ '--table-width': `${TABLE_HEADERS.length * 15}vw` }}
    >
      <Table className={styles.table}>
        <thead>
          <tr>
            {TABLE_HEADERS.map(({ key, label, width }) => (
              <th
                key={key}
                style={{ ...(width ? { width } : {}), ...(key !== 'actions' ? { cursor: 'pointer' } : {}) }}
                onClick={key !== 'actions' ? () => handleSort(key) : undefined}
              >{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedList.map((item) => (
            <tr
              key={item.xp_key}
              onClick={openModal(item)}
              className={styles.tableRow}
            >
              <td>
                <span className={styles.groupName}>{item.name}</span>
              </td>
              <td>
                <span className={styles.sortValue}>{item.sort}</span>
              </td>
              <td>
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={handleDelete(item)}
                  title={`Удалить группу питания ${item.name}`}
                  aria-label={`Удалить группу питания ${item.name}`}
                >
                  <BiTrash size={20} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className={styles.actions}>
        <Button onClick={openModal()}>
          <IoIosAdd size={20} style={{ marginRight: 8 }} />
          Добавить
        </Button>
        <span className={styles.counter}>
          Всего: {sortedList.length}
        </span>
      </div>
    </div>
  )

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <h1>Группы питания</h1>
      </header>

      <main className={styles.content}>
        {loading && renderLoading()}
        {!loading && sortedList.length === 0 && renderEmptyState()}
        {!loading && sortedList.length > 0 && renderTable()}
      </main>

      <GroupNutritionModal
        open={isModalOpen}
        onClose={closeModal}
        data={selectedGroup}
        addMode={isAddMode}
        parallels={parallelList}
      />

      {confirmation && (
        <Confirmation
          top="30px"
          left="40px"
          title={`Вы уверены, что хотите удалить ${confirmation.name}?`}
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
          loading={isDeleting}
        />
      )}
    </div>
  )
}
